import { MaintenanceResponse } from '@/api/model/MaintenanceResponse.model.ts';

import { HTTP_STATUS } from './ErrorCode.model';
import { getKeycloakRedirectUrl } from './getKeycloakRedirectUrl.util';
import { HttpMethod } from './HttpMethod.model';
import { isAxiosError, isErrorWithMessage } from './isAxiosError.guard';
import { permissionErrorModalSingleton } from './permissionErrorModalSingleton.util';

declare module 'axios' {
  export interface AxiosRequestConfig {
    isReadOperation?: boolean;
  }
}

interface ErrorResponseData {
  'internal-error'?: number | string;
  maintenanceEndDate?: string;
  maintenanceMode?: boolean;
  message?: string;
  messageEn?: string;
  messagePl?: string;
}

type Return = (
  error: unknown,
  errorHandler: (error: string) => void,
) => Promise<unknown> | undefined;

const KEYCLOAK_REDIRECT_URL =
  window.location.origin + '/' + import.meta.env.VITE_KEYCLOAK_REDIRECT_URL;

const READ_METHODS: string[] = [HttpMethod.GET, HttpMethod.HEAD, HttpMethod.OPTIONS];

const isReadRequest = (method: string | undefined): boolean =>
  READ_METHODS.includes(method?.toLowerCase() ?? '');

const handleError = (error: unknown, errorHandler: (error: string) => void) => {
  if (typeof error === 'string') {
    errorHandler(error);
  } else if (isErrorWithMessage(error)) {
    errorHandler(error.message);
  }
};

export const httpErrorHandler: Return = async (error, errorHandler) => {
  if (!isAxiosError(error)) {
    handleError(error, errorHandler);
    throw error;
  }

  if (error.response?.status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
    const responseData = error.response?.data as MaintenanceResponse;
    const isMaintenanceMode =
      responseData?.maintenanceMode || error.response?.headers?.['x-maintenance-mode'] === 'true';

    if (isMaintenanceMode) {
      const maintenanceData = {
        maintenanceEndDate: responseData?.maintenanceEndDate,
        messageEn: responseData?.messageEn,
        messagePl: responseData?.messagePl,
      };

      sessionStorage.setItem('maintenanceData', JSON.stringify(maintenanceData));
      throw error;
    }
  }

  if (
    error.response?.status !== HTTP_STATUS.FORBIDDEN &&
    error.response?.status !== HTTP_STATUS.UNAUTHORIZED
  ) {
    handleError(error, errorHandler);
    throw error;
  }

  // Check for internal-error in response body
  const rawData: unknown = error.response?.data;
  let responseData: ErrorResponseData | undefined;

  // If response data is a Blob, parse it as JSON
  if (rawData instanceof Blob) {
    try {
      const text = await rawData.text();
      responseData = JSON.parse(text) as ErrorResponseData;
    } catch (e) {
      console.error('[httpErrorHandler] Failed to parse Blob:', e);
    }
  } else {
    responseData = rawData as ErrorResponseData | undefined;
  }

  const internalError = responseData?.['internal-error'];

  if (
    error.response?.status === HTTP_STATUS.UNAUTHORIZED &&
    (internalError === 4001 || internalError === '4001')
  ) {
    // User doesn't have access to the application - redirect to not-authorized page
    // But only if we're not already on that page (to prevent redirect loop)
    if (!window.location.pathname.includes('/not-authorized')) {
      console.log('No application access (internal-error 4001), redirecting to /not-authorized');
      window.location.replace('/not-authorized');
      return;
    }
    // If already on /not-authorized page, just throw the error
    throw error;
  }

  if (error.response?.status === HTTP_STATUS.FORBIDDEN) {
    if (!isReadRequest(error.config?.method) && !error.config?.isReadOperation) {
      permissionErrorModalSingleton.showError(responseData?.message);
    }

    throw error;
  }

  window.location.replace(
    getKeycloakRedirectUrl(KEYCLOAK_REDIRECT_URL, window.location.pathname, window.location.search),
  );
};
